import React, { useState, useRef, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  Typography,
  Button,
  Box,
  Input,
  FormControl,
  FormLabel,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  LinearProgress,
} from '@mui/joy';
import { UploadSimple as Upload, X, Camera, FileImage, Check, XCircle as AlertCircle, ArrowsClockwise as RefreshCw } from '@phosphor-icons/react';
import { usePhotoBatchUpload, type BatchUploadProgress } from '../../hooks/usePhotoBatchUpload';

interface OpportunityPhotoUploadModalProps {
  open: boolean;
  onClose: () => void;
  opportunityId: string;
}

// Get status icon for a file based on upload progress
const getFileStatus = (
  file: File,
  index: number,
  progress: BatchUploadProgress
): 'pending' | 'uploading' | 'success' | 'failed' => {
  if (progress.status === 'idle') return 'pending';
  if (progress.status === 'creating_batch') return 'pending';

  // Check if this file failed
  const failedFile = progress.failedFiles.find(f => f.file.name === file.name);
  if (failedFile) return 'failed';

  // Check if currently uploading this file
  if (progress.currentFile === file.name) return 'uploading';

  // Check if this file was already uploaded
  const processedCount = progress.uploaded + progress.failed;
  if (index < processedCount && !failedFile) return 'success';

  return 'pending';
};

export const OpportunityPhotoUploadModal: React.FC<OpportunityPhotoUploadModalProps> = ({
  open,
  onClose,
  opportunityId,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [description, setDescription] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { progress, uploadPhotos, retryFailed, reset, isUploading } = usePhotoBatchUpload();

  // Reset the upload state when modal closes
  useEffect(() => {
    if (!open) {
      reset();
      setSelectedFiles([]);
      setDescription('');
      setValidationError(null);
    }
  }, [open, reset]);

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const validFiles: File[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file) => {
      // Check if it's an image
      if (!file.type.startsWith('image/')) {
        errors.push(`${file.name} is not an image file`);
        return;
      }

      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        errors.push(`${file.name} is too large (max 10MB)`);
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      setValidationError(errors.join(', '));
    } else {
      setValidationError(null);
    }

    setSelectedFiles([...selectedFiles, ...validFiles]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setValidationError(null);

    const result = await uploadPhotos({
      opportunityId,
      files: selectedFiles,
      description: description.trim() || undefined,
    });

    // Only close if all uploads succeeded
    if (result.failed === 0) {
      setSelectedFiles([]);
      setDescription('');
      onClose();
    }
  };

  const handleRetry = async () => {
    const result = await retryFailed();

    if (result.failed === 0) {
      setSelectedFiles([]);
      setDescription('');
      onClose();
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      onClose();
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Calculate progress percentage
  const progressPercent = progress.total > 0
    ? Math.round((progress.uploaded / progress.total) * 100)
    : 0;

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog sx={{ minWidth: 600, p: 3 }}>
        <Typography level="h4" startDecorator={<Camera />}>
          Upload Photos {selectedFiles.length > 0 && `(${selectedFiles.length})`}
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* Upload Progress */}
        {isUploading && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography level="body-sm" fontWeight="lg">
                Uploading {progress.uploaded} of {progress.total} photos...
              </Typography>
              <Typography level="body-sm" color="neutral">
                {progressPercent}%
              </Typography>
            </Box>
            <LinearProgress
              determinate
              value={progressPercent}
              sx={{ height: 8, borderRadius: 4 }}
            />
            {progress.currentFile && (
              <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                Uploading: {progress.currentFile}
              </Typography>
            )}
          </Box>
        )}

        {/* Validation Error Alert */}
        {validationError && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {validationError}
          </Alert>
        )}

        {/* Upload Error Alert */}
        {progress.status === 'error' && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {progress.error || 'Failed to upload photos. Please try again.'}
          </Alert>
        )}

        {/* Partial Failure Alert */}
        {progress.status === 'partial_failure' && (
          <Alert color="warning" sx={{ mb: 2 }}>
            <Box>
              <Typography level="body-sm" fontWeight="lg">
                {progress.uploaded} of {progress.total} photos uploaded successfully.
              </Typography>
              <Typography level="body-sm">
                {progress.failed} photo{progress.failed !== 1 ? 's' : ''} failed to upload.
              </Typography>
            </Box>
          </Alert>
        )}

        {/* Success Alert */}
        {progress.status === 'completed' && progress.uploaded > 0 && (
          <Alert color="success" sx={{ mb: 2 }}>
            Successfully uploaded {progress.uploaded} photo{progress.uploaded !== 1 ? 's' : ''}.
          </Alert>
        )}

        {/* Drop Zone and Selected Files - Side by Side */}
        <Box sx={{
          display: 'flex',
          gap: 2,
          mb: 2,
          flexWrap: { xs: 'wrap', sm: 'nowrap' }
        }}>
          {/* Drag and Drop Area */}
          <Box
            sx={{
              flex: 1,
              minWidth: 280,
              border: '2px dashed',
              borderColor: isDragOver ? 'primary.500' : 'divider',
              borderRadius: 'sm',
              p: 4,
              textAlign: 'center',
              cursor: isUploading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              backgroundColor: isDragOver ? 'primary.50' : 'transparent',
              opacity: isUploading ? 0.5 : 1,
              '&:hover': {
                borderColor: isUploading ? 'divider' : 'primary.300',
                backgroundColor: isUploading ? 'transparent' : 'background.level1',
              },
            }}
            onDrop={isUploading ? undefined : handleDrop}
            onDragOver={isUploading ? undefined : handleDragOver}
            onDragLeave={isUploading ? undefined : handleDragLeave}
            onClick={isUploading ? undefined : () => fileInputRef.current?.click()}
          >
            <Upload size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
            <Typography level="body-lg" sx={{ mb: 1 }}>
              Drag & drop images here, or click to browse
            </Typography>
            <Typography level="body-sm" color="neutral">
              Maximum file size: 10MB per image
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => handleFileSelect(e.target.files)}
              disabled={isUploading}
            />
          </Box>

          {/* Selected Files */}
          {selectedFiles.length > 0 && (
            <Box sx={{
              width: { xs: '100%', sm: 280 },
              flexShrink: 0
            }}>
              <Typography level="body-sm" fontWeight="lg" sx={{ mb: 1 }}>
                Selected Files ({selectedFiles.length}):
              </Typography>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                maxHeight: 200,
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 'sm',
                p: 1
              }}>
                {selectedFiles.map((file, index) => {
                  const status = getFileStatus(file, index, progress);
                  return (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                        borderRadius: 'sm',
                        backgroundColor: status === 'failed' ? 'danger.softBg' : 'background.level1',
                      }}
                    >
                      {/* Status icon */}
                      {status === 'success' && <Check size={18} color="var(--joy-palette-success-500)" />}
                      {status === 'failed' && <AlertCircle size={18} color="var(--joy-palette-danger-500)" />}
                      {status === 'uploading' && <CircularProgress size="sm" sx={{ '--CircularProgress-size': '18px' }} />}
                      {status === 'pending' && <FileImage size={18} />}

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography level="body-sm" noWrap>
                          {file.name}
                        </Typography>
                        <Typography level="body-xs" color="neutral">
                          {formatFileSize(file.size)}
                        </Typography>
                      </Box>
                      {!isUploading && status !== 'success' && (
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="neutral"
                          onClick={() => removeFile(index)}
                        >
                          <X size={16} />
                        </IconButton>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>

        {/* Description Field */}
        {selectedFiles.length > 0 && (
          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Description (optional)</FormLabel>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add a description for these photos..."
              disabled={isUploading}
            />
          </FormControl>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, mt: 3, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleClose}
            disabled={isUploading}
          >
            {progress.status === 'partial_failure' ? 'Close' : 'Cancel'}
          </Button>

          {/* Retry button for failed uploads */}
          {progress.status === 'partial_failure' && progress.failedFiles.length > 0 && (
            <Button
              variant="outlined"
              color="warning"
              onClick={handleRetry}
              disabled={isUploading}
              startDecorator={<RefreshCw size={18} />}
            >
              Retry Failed ({progress.failedFiles.length})
            </Button>
          )}

          {/* Main upload button */}
          {progress.status !== 'partial_failure' && progress.status !== 'completed' && (
            <Button
              variant="solid"
              color="primary"
              onClick={handleUpload}
              disabled={selectedFiles.length === 0 || isUploading}
              startDecorator={isUploading ? <CircularProgress size="sm" /> : <Upload size={16} />}
            >
              {isUploading ? 'Uploading...' : 'Upload Photos'}
            </Button>
          )}
        </Box>
      </ModalDialog>
    </Modal>
  );
};
